import { Controller, useForm } from "react-hook-form";
import useAccountEnhanced from "src/hooks/use-account-enhanced";
import { Section } from "../../types/section";
import dayjs from "dayjs";
import { SignaturePad } from "src/components/signature-pad";
import {
    driverRoadTestMutations,
    driverRoadTestQueryOptions,
} from "src/api/mvte/driver-road-test-query-options";
import { SavedExam } from "../../types/exam";
import { TenstreetApplicant } from "src/types/tenstreet-applicant";
import { getFullName } from "../../utils/get-full-name";
import { DownloadCertificateButton } from "../download-certificate-button";
import { toast } from "react-hot-toast";
import clsx from "clsx";

type ExamFooterProps = {
    isExamCompleted: boolean;
    totalSections: number;
    onCompleteExam: (completedExam: SavedExam) => void;
    onExit: () => void;
    canExamBeCompleted: boolean;
    driver: TenstreetApplicant;
    exam: SavedExam;
};

type CompleteExamFormData = {
    result: Section["result"];
    signatureImageBase64: string;
};

export function ExamFooter({
    isExamCompleted,
    totalSections,
    onCompleteExam,
    onExit,
    canExamBeCompleted,
    driver,
    exam,
}: ExamFooterProps) {
    const account = useAccountEnhanced();
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        control,
        formState: { isSubmitting },
        setError,
    } = useForm<CompleteExamFormData>();
    const updateExamMutation = driverRoadTestQueryOptions.useUpdateExam();
    const completeExamMutation = driverRoadTestMutations.useCompleteExam();

    async function onSubmit(data: CompleteExamFormData) {
        try {
            const completedExam = await completeExamMutation.mutateAsync({
                body: {
                    exam: {
                        ...exam,
                        examStatus: "COMPLETED",
                        examResult: data.result,
                    },
                    driver,
                    signatureImageBase64: data.signatureImageBase64,
                },
            });

            const modal = document.getElementById(
                "exam_completion_modal",
            ) as HTMLDialogElement;
            modal?.close();

            onCompleteExam(completedExam);
        } catch (error: unknown) {
            setError("root", {
                type: "manual",
                message:
                    error instanceof Error
                        ? error.message
                        : "Unexpected error occurred while saving the exam",
            });
        }
    }

    async function handleSaveAndExit() {
        try {
            await updateExamMutation.mutateAsync({
                body: {
                    exam: {
                        ...exam,
                        examStatus: "SAVED",
                    },
                },
            });
        } catch (error: unknown) {
            toast.error(
                `Error saving exam: ${
                    error instanceof Error ? error.message : "Unexpected error"
                }`,
            );
            return;
        }

        onExit();
    }

    function handleModalClose() {
        reset();
    }

    return (
        <>
            <div className="p-3 border-t border-base-300 bg-gray-50 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                    <span className="font-medium">{totalSections}</span> total
                </div>
                <div className="flex gap-2">
                    {isExamCompleted ? (
                        <>
                            {exam.certificateName && (
                                <DownloadCertificateButton
                                    certificateName={exam.certificateName}
                                />
                            )}
                            <button
                                className="btn btn-primary btn-sm px-6"
                                onClick={onExit}
                            >
                                Exit
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className={clsx(
                                    "btn btn-primary btn-sm px-6",
                                    updateExamMutation.isPending &&
                                        "pointer-events-none opacity-50",
                                )}
                                onClick={handleSaveAndExit}
                            >
                                {updateExamMutation.isPending ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs" />
                                        Saving
                                    </>
                                ) : (
                                    "Save & Exit"
                                )}
                            </button>
                            <button
                                disabled={!canExamBeCompleted}
                                className="btn btn-primary btn-sm px-6"
                                onClick={() => {
                                    const modal = document.getElementById(
                                        "exam_completion_modal",
                                    ) as HTMLDialogElement;
                                    modal?.showModal();
                                }}
                            >
                                Complete Exam
                            </button>
                        </>
                    )}
                </div>
            </div>

            <dialog
                id="exam_completion_modal"
                className="modal"
                onClose={handleModalClose}
            >
                <div className="modal-box">
                    <h3 className="font-bold text-lg mb-4">Complete Exam</h3>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        {errors.root && (
                            <div className="alert alert-error mb-4">
                                <span>{errors.root.message}</span>
                            </div>
                        )}
                        <div className="mb-4">
                            <fieldset className="fieldset">
                                <legend className="fieldset-legend">
                                    Exam Result
                                </legend>
                                <select
                                    className={`select select-bordered w-full ${
                                        errors.result ? "select-error" : ""
                                    }`}
                                    {...register("result", {
                                        required: "Result is required",
                                    })}
                                >
                                    <option value="">Select a result</option>
                                    <option value="Qualified">Qualified</option>
                                    <option value="Qualified with additional training">
                                        Qualified with additional training
                                    </option>
                                    <option value="Not Qualified">
                                        Not Qualified
                                    </option>
                                </select>
                                {errors.result && (
                                    <span className="text-error text-sm mt-1">
                                        {errors.result.message}
                                    </span>
                                )}
                            </fieldset>
                            <fieldset className="fieldset">
                                <legend className="fieldset-legend">
                                    Signature
                                </legend>
                                <Controller
                                    name="signatureImageBase64"
                                    control={control}
                                    render={({
                                        field: { onChange, value },
                                    }) => {
                                        return (
                                            <SignaturePad
                                                value={value}
                                                onChange={onChange}
                                                error={
                                                    errors.signatureImageBase64
                                                        ?.message
                                                }
                                            />
                                        );
                                    }}
                                    rules={{
                                        required: "Signature is required",
                                    }}
                                />
                            </fieldset>
                        </div>

                        <div className="mb-4">
                            {/* TODO: Validate testDateUTC is not null and milesDriven is not null */}
                            <p className="text-sm text-gray-600">
                                I, {account.account.name}, hereby certify that{" "}
                                {getFullName(
                                    driver.firstName,
                                    driver.middleName,
                                    driver.lastName,
                                )}{" "}
                                was given a road test under my supervision on{" "}
                                {dayjs(exam.examCompletedDateUTC).format(
                                    "MM/DD/YYYY",
                                )}{" "}
                                consisting of approximately {exam.milesDriven}{" "}
                                miles driven. It is my considered opinion that
                                this driver possesses sufficient driving skill
                                to operate {exam.typeOfPowerUnit}{" "}
                                {exam.typeOfTrailer} safely.
                            </p>
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost mr-2"
                                onClick={() => {
                                    const modal = document.getElementById(
                                        "exam_completion_modal",
                                    ) as HTMLDialogElement;
                                    modal?.close();
                                }}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                {isSubmitting ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs" />
                                        Loading
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </>
    );
}
